import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkingHoursDto } from '@motoschool/shared';
import { minutesToTime, WEEKDAYS } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageLoader,
  Select,
  SuccessNotice,
  cn,
} from '../components/ui';
import { useTeachers } from '../lib/useTeachers';

interface WorkingHoursResponse {
  days: WorkingHoursDto[];
  timeZone: string;
}

/**
 * Границы оси в схеме недели. Совпадают с вариантами времени в списках:
 * рисовать полосу шире, чем можно выбрать, значит показывать пустоту,
 * до которой не дотянуться.
 */
const AXIS_START = 6 * 60;
const AXIS_END = 22 * 60;

/** Варианты времени: с 6 утра до 22 вечера с шагом в полчаса. */
const TIME_OPTIONS = Array.from(
  { length: (AXIS_END - AXIS_START) / 30 + 1 },
  (_, index) => AXIS_START + index * 30,
);

const percent = (minute: number): number =>
  ((minute - AXIS_START) / (AXIS_END - AXIS_START)) * 100;

/** «3 дня» — склонение для итога по неделе. */
function pluralDays(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}

/**
 * Недельный график инструктора.
 *
 * Это единственное, что инструктор задаёт по времени: расписание он не
 * нарезает, а свободное время считается как график минус занятое. Поэтому
 * неделя показана целиком полосами — по ним сразу видно, где день короткий,
 * а где выходной, и не приходится сверять семь пар выпадающих списков.
 *
 * Свой график ведёт сам инструктор, чужой — администратор: он приходит сюда
 * из карточки сотрудника с `?teacher=<id>`.
 */
export function WorkingHoursPage(): React.JSX.Element {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const isAdmin = hasRole('ADMIN');

  const teacherId = params.get('teacher') ?? (user?.role === 'TEACHER' ? user.id : '');

  const { data: teachers } = useTeachers(isAdmin);
  const teacherOptions = teachers?.items ?? [];

  const [days, setDays] = useState<WorkingHoursDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['working-hours', teacherId],
    queryFn: () => api.get<WorkingHoursResponse>(`/users/${teacherId}/working-hours`),
    enabled: Boolean(teacherId),
  });

  useEffect(() => {
    if (data) setDays(data.days);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put<WorkingHoursResponse>(`/users/${teacherId}/working-hours`, { days }),
    onSuccess: async () => {
      setError(null);
      setIsSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['working-hours', teacherId] });
      // График виден гостю как свободное время — публичный кеш тоже устарел.
      await queryClient.invalidateQueries({ queryKey: ['public'] });
      setTimeout(() => setIsSaved(false), 2500);
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось сохранить график');
    },
  });

  const byWeekday = new Map(days.map((day) => [day.weekday, day]));

  const updateDay = (weekday: number, patch: Partial<WorkingHoursDto>): void => {
    setDays((previous) =>
      previous.map((day) => (day.weekday === weekday ? { ...day, ...patch } : day)),
    );
  };

  /** Одинаковые часы на все рабочие дни — самый частый случай. */
  const applyToAll = (source: WorkingHoursDto): void => {
    setDays((previous) =>
      previous.map((day) =>
        day.isEnabled
          ? { ...day, startMinute: source.startMinute, endMinute: source.endMinute }
          : day,
      ),
    );
  };

  const workingDays = days.filter((day) => day.isEnabled && day.endMinute > day.startMinute);
  const weekMinutes = workingDays.reduce((sum, day) => sum + (day.endMinute - day.startMinute), 0);
  const hasBrokenDay = days.some((day) => day.isEnabled && day.endMinute <= day.startMinute);

  // Пока ничего не поменяли, сохранять нечего — кнопка не должна звать зря.
  const isDirty = useMemo(
    () => data !== undefined && JSON.stringify(days) !== JSON.stringify(data.days),
    [days, data],
  );

  if (!teacherId) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-semibold">Часы работы</h1>
        {isAdmin ? (
          <Card className="space-y-3">
            <p className="text-sm text-muted">Выберите инструктора, чей график нужно настроить.</p>
            <Select
              value=""
              onChange={(event) => setParams({ teacher: event.target.value })}
              aria-label="Инструктор"
            >
              <option value="">Выберите инструктора</option>
              {teacherOptions.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </option>
              ))}
            </Select>
          </Card>
        ) : (
          <EmptyState
            title="График есть только у инструкторов"
            description="Ваша роль расписание не ведёт."
          />
        )}
      </div>
    );
  }

  if (isLoading) return <PageLoader />;

  const teacherName = teacherOptions.find((item) => item.id === teacherId)?.name;

  return (
    <div className="space-y-5 pb-4">
      <div>
        <h1 className="text-xl font-semibold">Часы работы</h1>
        <p className="text-sm text-muted">
          {teacherId === user?.id ? 'Ваш недельный график' : `График — ${teacherName ?? 'инструктор'}`}
          . Ученик выбирает время только внутри этих часов.
        </p>
      </div>

      {error && <ErrorNotice message={error} />}
      {isSaved && <SuccessNotice>График сохранён</SuccessNotice>}

      {/* Итог по неделе: сколько часов школа вообще может продать */}
      <Card className="flex items-baseline justify-between gap-3 p-3">
        <span className="text-sm text-muted">В неделю</span>
        <span className="text-lg font-semibold tabular-nums">
          {Math.round(weekMinutes / 60)} ч · {workingDays.length} {pluralDays(workingDays.length)}
        </span>
      </Card>

      {/*
        Шкала над полосами: без неё непонятно, что за отрезок нарисован.
        Поля те же, что у карточек дня, иначе метки часов разъедутся с полосами.
      */}
      <div className="flex justify-between px-3 text-xs tabular-nums text-muted">
        <span>{minutesToTime(AXIS_START)}</span>
        <span>14:00</span>
        <span>{minutesToTime(AXIS_END)}</span>
      </div>

      <div className="space-y-2">
        {WEEKDAYS.map((weekday) => {
          const day = byWeekday.get(weekday.value);
          if (!day) return null;

          const isBroken = day.isEnabled && day.endMinute <= day.startMinute;

          return (
            <Card
              key={weekday.value}
              className={cn('p-3', !day.isEnabled && 'border-dashed bg-canvas')}
            >
              <div className="flex items-center justify-between gap-3">
                <label className="flex min-h-11 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={day.isEnabled}
                    onChange={(event) =>
                      updateDay(weekday.value, { isEnabled: event.target.checked })
                    }
                    className="size-5 accent-brand-600"
                    aria-label={weekday.label}
                  />
                  <span className="text-sm font-medium">{weekday.label}</span>
                </label>

                <span
                  className={cn(
                    'shrink-0 text-xs tabular-nums',
                    day.isEnabled ? 'text-ink' : 'text-muted',
                  )}
                >
                  {day.isEnabled
                    ? `${minutesToTime(day.startMinute)}—${minutesToTime(day.endMinute)}`
                    : 'выходной'}
                </span>
              </div>

              {/* Полоса рабочего времени на общей для всех дней шкале */}
              <div className="relative mt-1 h-5 overflow-hidden rounded-lg bg-canvas ring-1 ring-line">
                {day.isEnabled && !isBroken && (
                  <div
                    className="absolute inset-y-0 rounded-md bg-night"
                    style={{
                      left: `${percent(day.startMinute)}%`,
                      width: `${percent(day.endMinute) - percent(day.startMinute)}%`,
                    }}
                  />
                )}
              </div>

              {day.isEnabled && (
                <div className="mt-3 flex items-center gap-2">
                  <Select
                    value={day.startMinute}
                    onChange={(event) =>
                      updateDay(weekday.value, { startMinute: Number(event.target.value) })
                    }
                    className="min-h-11"
                    aria-label={`${weekday.label}: начало`}
                  >
                    {TIME_OPTIONS.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {minutesToTime(minutes)}
                      </option>
                    ))}
                  </Select>

                  <span className="text-muted" aria-hidden>
                    —
                  </span>

                  <Select
                    value={day.endMinute}
                    onChange={(event) =>
                      updateDay(weekday.value, { endMinute: Number(event.target.value) })
                    }
                    className="min-h-11"
                    aria-label={`${weekday.label}: конец`}
                  >
                    {TIME_OPTIONS.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {minutesToTime(minutes)}
                      </option>
                    ))}
                  </Select>

                  <button
                    type="button"
                    onClick={() => applyToAll(day)}
                    className="min-h-11 shrink-0 rounded-xl px-3 text-xs font-medium text-brand-600 hover:bg-brand-50"
                    title="Поставить эти часы всем рабочим дням"
                  >
                    всем
                  </button>
                </div>
              )}

              {isBroken && (
                <p className="mt-2 text-xs text-danger">
                  Конец рабочего дня должен быть позже начала
                </p>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted">
        Занятия, которые уже стоят вне новых часов, никуда не денутся — график
        влияет только на то, что ученик может выбрать дальше.{' '}
        <Link to="/schedule" className="font-medium text-brand-600">
          Открыть расписание
        </Link>
      </p>

      {/*
        Кнопка обычная, не липкая: снизу в кабинете уже стоит фиксированная
        навигация, и прилипшая кнопка ушла бы прямо под неё.
      */}
      <Button
        fullWidth
        isLoading={save.isPending}
        disabled={!isDirty || hasBrokenDay}
        onClick={() => save.mutate()}
      >
        {isDirty ? 'Сохранить' : 'Всё сохранено'}
      </Button>
    </div>
  );
}
